/**
 * Email validation utilities for APA Life authentication
 */

// APA Life domain constant
export const APA_LIFE_DOMAIN = '@apalife.co.ke';

/**
 * Validates if email has correct format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;

  const parts = email.split('@');
  if (parts.length !== 2) return false;

  const [localPart, domain] = parts;
  if (!localPart || !domain) return false;

  if (/\s/.test(email) || /\.\./.test(email)) return false;

  const domainParts = domain.split('.');
  if (domainParts.length < 2) return false;

  return domainParts.every((part) => part && /^[a-zA-Z0-9-]+$/.test(part))
         && /^[^\s@]+$/.test(localPart) && !localPart.startsWith('.') && !localPart.endsWith('.');
};

/**
 * Validates if email belongs to APA Life domain
 * @param {string} email - Email to validate
 * @returns {boolean} - True if email ends with @apalife.co.ke
 */
export const isApaLifeEmail = (email) => {
  if (!email) return false;
  return email.toLowerCase().endsWith(APA_LIFE_DOMAIN);
};

/**
 * Validates if email is both valid format and APA Life domain
 * @param {string} email - Email to validate
 * @returns {boolean} - True if email is valid and from APA Life domain
 */
export const validateApaLifeEmail = (email) => isValidEmail(email) && isApaLifeEmail(email);

/**
 * Gets email validation error message
 * @param {string} email - Email to validate
 * @returns {string|null} - Error message or null if valid
 */
export const getEmailValidationError = (email) => {
  if (!email) {
    return 'Email is required';
  }

  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }

  if (!isApaLifeEmail(email)) {
    return 'Only @apalife.co.ke email addresses are allowed';
  }

  return null;
};

/**
 * Validates if input is either a valid agent code or valid APA Life email
 * @param {string} input - Input to validate (agent code or email)
 * @returns {boolean} - True if valid agent code or APA Life email
 */
export const validateAgentCodeOrEmail = (input) => {
  if (!input) return false;

  if (isValidEmail(input)) {
    return isApaLifeEmail(input);
  }

  return /^[a-zA-Z0-9]+$/.test(input);
};

/**
 * Email validation rules for form validation
 * @returns {Array} - Array of validation rules
 */
export const getEmailValidationRules = () => [
  (v) => !!v || 'Email is required',
  (v) => isValidEmail(v) || 'Please enter a valid email address',
  (v) => isApaLifeEmail(v) || 'Only @apalife.co.ke email addresses are allowed',
];

/**
 * Combined validation rules for agent code or email
 * @returns {Array} - Array of validation rules
 */
export const getAgentCodeOrEmailValidationRules = () => [
  (v) => !!v || 'Agent Code or Email is required',
  (v) => validateAgentCodeOrEmail(v) || 'Enter a valid agent code or @apalife.co.ke email',
];
